import React, { useState, useEffect } from "react";
import styles from "./AccountDetail.module.css";
import { mcbWallets, mcbPartnerWallets } from "../../api";
import DataLoader from "../../Common/DataLoader/DataLoader";

const Wallets = ({ userId, apiType, accountStatus, onBalanceChange, onHasBalance, refreshKey }) => {
  const [walletsData, setWalletsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (accountStatus === "PENDING") {
      setLoading(false);
      onHasBalance?.(false);
      return;
    }
    const getWalletsData = async () => {
      try {
        setLoading(true);
        const response =
          apiType === "partner"
            ? await mcbPartnerWallets(userId)
            : await mcbWallets(userId);

        if (response?.status === 200 && response?.data?.data?.accounts) {
          const accounts = response.data.data.accounts;
          setWalletsData(accounts);
          setError(null);

          const usdWallet = accounts.find(
            (wallet) => wallet?.vendorCurrency === "USD",
          );

          if (usdWallet && onBalanceChange) {
            const usdBalance =
              (parseFloat(usdWallet?.balance) || 0) -
              (parseFloat(usdWallet?.holdBalance) || 0);

            onBalanceChange(usdBalance);
          }
          const hasNonZeroBalance = accounts.some(
            (wallet) => (parseFloat(wallet?.balance) || 0) !== 0,
          );
          onHasBalance?.(hasNonZeroBalance);
        } else {
          setError(
            response?.data?.message || "Failed to fetch wallets details",
          );
          onHasBalance?.(false);
        }
      } catch (err) {
        setError("Something went wrong while fetching wallets details");
        console.error(err);
        onHasBalance?.(false);
      } finally {
        setLoading(false);
      }
    };

    getWalletsData();
  }, [userId, apiType, accountStatus, refreshKey]);
  if (accountStatus === "PENDING") {
    return (
      <div className={styles.noData}>
        Wallet balances will be available after the Global Account is approved.
      </div>
    );
  }
  if (!walletsData || walletsData.length === 0) {
    return <div className={styles.noData}>No Global account found</div>;
  }

  const getBalanceColor = (balance) => {
    if (balance > 0) return styles.positiveBalance;
    if (balance < 0) return styles.negativeBalance;
    return styles.zeroBalance;
  };
  const formatAmount = (value) => {
    const num = Number(value);
    if (!isNaN(num) && num === 0) {
      return "0.00";
    }
    return value ?? "0.00";
  };

  return (
    <DataLoader isLoading={loading} error={error}>
      <div className={styles.walletsContainer}>
        <div className={styles.walletsGrid}>
          {walletsData.map((wallet) => (
            <div key={wallet.accountId} className={styles.walletCard}>
              <div className={styles.walletBalance}>
                <span className={styles.balanceAmount}>
                  {wallet?.vendorCurrency}
                  {"  "}
                </span>
                <span
                  className={`${styles.balanceAmount} ${getBalanceColor(
                    Number(wallet?.balance || 0) -
                      Number(wallet?.holdBalance || 0),
                  )}`}
                >
                  {formatAmount(
                    (
                      Number(wallet?.balance || 0) -
                      Number(wallet?.holdBalance || 0)
                    ).toLocaleString(),
                  )}
                </span>
              </div>

              <div className={styles.walletDetails}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Wallet ID:</span>
                  <span className={styles.detailValue}>
                    {wallet?.accountId}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Hold Balance:</span>
                  <span
                    className={`${styles.detailValue} ${getBalanceColor(
                      wallet?.holdBalance,
                    )}`}
                  >
                    {formatAmount(wallet?.holdBalance)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Pending Balance:</span>
                  <span
                    className={`${styles.detailValue} ${getBalanceColor(
                      wallet?.pendingBalance,
                    )}`}
                  >
                    {formatAmount(wallet?.pendingBalance)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Reserved Balance:</span>
                  <span
                    className={`${styles.detailValue} ${getBalanceColor(
                      wallet?.reservedBalance,
                    )}`}
                  >
                    {formatAmount(wallet?.reservedBalance)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Available:</span>
                  <span
                    className={`${styles.detailValue} ${getBalanceColor(
                      wallet?.availableAmount,
                    )}`}
                  >
                    {formatAmount(wallet?.availableAmount)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DataLoader>
  );
};

export default Wallets;
